import React, { useState } from 'react'
import {
    FormControlLabel,
    Switch as MuiSwitch,
    TextField,
    MenuItem,
} from '@material-ui/core'
import PersonIcon from '@material-ui/icons/Person'
import BusinessIcon from '@material-ui/icons/Business'
import Button from 'components/shared/Button'
import AuthService from 'tools/AuthService'
import HealthcareCheckDetail from './SavedRecord/HealthcareCheckDetail'
import SourceTabs from './SourceTabs'
import { makeStyles } from '@material-ui/core/styles'
import {
    healthcareSearch,
    healthcareSearchDemoMode,
    getHealthcareSearchHistory,
    getHealthcareSearchResult,
    getHealthcareSearchSavedRecords,
    deleteHealthcareSearchSavedRecord,
} from 'actions/healthcareSearch'
import { Toast } from 'components/shared/Toaster'
import { useAppContext } from 'context/AppContext'
import CreditCost from 'components/shared/CreditCost'

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS',
    'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC',
    'ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
]

// A form field's share of the search row; below its basis it wraps to the next line.
const FIELD = { flex: '1 1 140px' }

const useStyles = makeStyles(theme => ({
    root: { padding: theme.spacing(2) },
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 296px',
        gap: theme.spacing(2),
        alignItems: 'start',
        // Too narrow for the rail beside the results: previous searches move below.
        '@media (max-width: 1099px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    storedBanner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1, 2),
        marginBottom: theme.spacing(2),
        background: 'var(--bg-canvas)',
        borderRadius: 6,
        fontSize: 13,
        color: 'var(--fg-secondary)',
    },
    header: { marginBottom: theme.spacing(2) },
    title: { fontSize: 24, fontWeight: 600, color: 'var(--neutral-900)', marginBottom: 4 },
    subtitle: { fontSize: 13, color: 'var(--fg-secondary)' },
    formCard: {
        background: '#fff',
        padding: theme.spacing(2),
        borderRadius: 6,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginBottom: theme.spacing(2),
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1.4fr 1fr 0.8fr 1fr',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    formActions: { display: 'flex', alignItems: 'center', gap: theme.spacing(2) },
    searchBtn: {
        background: 'var(--neutral-900)',
        color: '#fff',
        '&:hover': { background: '#1a3d70' },
        '&.Mui-disabled': { background: '#c8ced8', color: '#fff' },
    },
    resultsHeader: { display: 'flex', alignItems: 'baseline', gap: theme.spacing(2), marginBottom: theme.spacing(2) },
    resultsCount: { fontSize: 18, fontWeight: 600, color: 'var(--neutral-900)' },
    resultsBreakdown: { fontSize: 13, color: 'var(--fg-secondary)' },
    sourceStrip: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(3),
        padding: theme.spacing(1, 1.5),
        background: 'var(--bg-canvas)',
        borderRadius: 6,
        fontSize: 12,
    },
    sourceChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        borderRadius: 12,
        fontWeight: 600,
        letterSpacing: '.02em',
    },
    sourceOk: { background: '#e6f4ea', color: '#1e6132' },
    sourceError: { background: '#fdecea', color: '#8b1a11' },
    sourceDisabled: { background: '#eceff3', color: 'var(--fg-secondary)' },
    empty: {
        textAlign: 'center',
        padding: theme.spacing(6, 2),
        color: 'var(--fg-secondary)',
        fontSize: 14,
    },
    coverage: {
        fontSize: 12,
        color: 'var(--fg-secondary)',
        marginTop: theme.spacing(-2),
        marginBottom: theme.spacing(3),
        padding: theme.spacing(0, 1.5),
    },
    coverageToggle: {
        background: 'none',
        border: 'none',
        padding: 0,
        marginLeft: 6,
        color: 'var(--neutral-900)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    disclosure: {
        fontSize: 12,
        color: 'var(--fg-secondary)',
        marginTop: theme.spacing(2),
        fontStyle: 'italic',
    },
}))

// Split a stored search name back into the form fields for its mode ("LAST, FIRST" for a person).
const nameFields = (name, mode) => {
    if (mode === 'business') return { name: name || '', firstName: '', lastName: '' }
    const [last = '', first = ''] = (name || '').split(',').map(x => x.trim())
    return { name: '', firstName: first, lastName: last }
}

const HealthcareSearch = ({ prefillTargets, applicantId, creditCosts }) => {
    const classes = useStyles()
    const { isDemoMode } = useAppContext()
    const { creditUsageEnabled } = AuthService.getClientFeatures()
    // Cost per Healthcare Search run — sourced from the backend billing table (RequestType
    // HEALTHCARE_SEARCH = 1 credit). Rendered as a credit icon inside the Search button (matching
    // every other search tab) so the operator sees the debit before clicking. Free plans / demo
    // mode still reflect the same number for consistency; billing itself is enforced server-side.
    const healthcareSearchCost = React.useMemo(() => {
        const row = (creditCosts || []).find(c => c?.requestType === 'HEALTHCARE_SEARCH')
        return row?.creditCost != null ? row.creditCost : null
    }, [creditCosts])
    const [form, setForm] = useState({
        name: '', firstName: '', lastName: '', npi: '', state: '', dob: '', includeInactive: false,
    })
    // 'individual' | 'business' — the Person / Business switch. Individuals are sent as
    // "LAST, FIRST" (the only shape the sources search as a person); a business name goes as is.
    // DOB is only meaningful for individuals (LEIE), so it's hidden for a business.
    const [searchMode, setSearchMode] = useState('individual')
    const [loading, setLoading] = useState(false)
    const [response, setResponse] = useState(null)
    const [hasSearched, setHasSearched] = useState(false)
    // What the shown result was searched as, for OFAC's "why it matched" and the clear lines.
    const [searched, setSearched] = useState(null)
    // savedRecords: applicant-scoped saved cards, drives both the Saved-records panel and the
    // per-card Save/Saved toggle. Only fetched when the component is mounted with an applicantId
    // (embedded-in-application mode); the standalone /healthcare-search page leaves it empty.
    const [savedRecords, setSavedRecords] = useState([])
    const [savedRefreshTick, setSavedRefreshTick] = useState(0)
    // Past searches on this application; reopening one shows its stored result for free.
    const [history, setHistory] = useState([])
    const [historyTick, setHistoryTick] = useState(0)
    const [activeSearchId, setActiveSearchId] = useState(null)

    // Without an application this is Quick Search, whose history lists at the client level.
    React.useEffect(() => {
        getHealthcareSearchHistory({ applicationId: applicantId, isDemoMode })()
            .then(res => setHistory(Array.isArray(res) ? res : []))
            .catch(() => {})
    }, [applicantId, isDemoMode, historyTick])

    React.useEffect(() => {
        if (!applicantId) { setSavedRecords([]); return }
        getHealthcareSearchSavedRecords({ applicationId: applicantId })()
            .then(res => setSavedRecords(Array.isArray(res) ? res : []))
            .catch(() => {})
    }, [applicantId, savedRefreshTick])


    const handleDeleteSaved = (recordId, displayName) => {
        if (!applicantId) return
        deleteHealthcareSearchSavedRecord({ applicationId: applicantId, recordId })()
            .then(() => {
                Toast({ type: 'success', message: `Removed: ${displayName || 'record'}` })
                setSavedRefreshTick(k => k + 1)
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to remove record' }))
    }

    const setField = (k) => (e) => {
        const v = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value
        setForm(f => ({ ...f, [k]: v }))
    }

    // Clicking a prefill chip loads the target's name (and state / NPI, when the target
    // carries them) into the form; wipes DOB so the operator opts back in explicitly, and
    // leaves the includeInactive toggle alone. When the business row has a saved NPI (added
    // to the create-application form for HEALTHCARE_SEARCH clients), the chip does an
    // NPI-first exact match — no fuzzy search, no ambiguity.
    const applyPrefill = (target) => {
        if (!target) return
        setSearchMode(target.kind === 'business' ? 'business' : 'individual')
        setForm(f => ({
            ...f,
            ...nameFields(target.name, target.kind === 'business' ? 'business' : 'individual'),
            npi: target.npi || '',
            state: target.state || '',
            dob: '',
        }))
    }

    const prefillOptions = React.useMemo(() => {
        if (!prefillTargets) return []
        const out = []
        if (prefillTargets.business) {
            out.push({
                label: 'Business',
                name: prefillTargets.business,
                state: prefillTargets.businessState || null,
                npi: prefillTargets.businessNpi || null,
                kind: 'business',
            })
        }
        for (const o of (prefillTargets.owners || [])) {
            const combined = [o.lastName, o.firstName].filter(Boolean).join(', ')
            const label = o.label || [o.firstName, o.lastName].filter(Boolean).join(' ') || 'Owner'
            if (combined) out.push({ label, name: combined, kind: 'owner' })
        }
        return out
    }, [prefillTargets])

    // A person always goes out with a comma ("LAST, FIRST", "LAST," or ", FIRST") so every source
    // searches individuals; without one the backend treats the name as a business.
    const searchName = () => {
        if (searchMode === 'business') return form.name.trim()
        const last = form.lastName.trim()
        const first = form.firstName.trim()
        if (!last && !first) return ''
        return `${last},${first ? ` ${first}` : ''}`
    }

    const canSearch = () => {
        // DOB only counts when the active mode sends it (businesses don't).
        return !!(searchName() || form.npi.trim() || form.state.trim()
            || (searchMode !== 'business' && form.dob.trim()))
    }

    const onSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault()
        if (!canSearch()) {
            Toast({ type: 'error', message: 'Provide at least one search field' })
            return
        }
        const payload = {}
        if (searchName()) payload.name = searchName()
        if (form.npi.trim())   payload.npi = form.npi.trim()
        if (form.state.trim()) payload.state = form.state.trim()
        if (searchMode !== 'business' && form.dob.trim()) {
            payload.dob = form.dob.trim().replace(/-/g, '')  // YYYY-MM-DD → YYYYMMDD for LEIE
        }
        if (form.includeInactive) payload.includeInactive = true

        setLoading(true)
        setResponse(null)
        setHasSearched(true)
        setActiveSearchId(null)
        setSearched({ name: readableName(payload.name || payload.npi), npi: payload.npi || '', at: new Date().toISOString() })
        const runner = isDemoMode ? healthcareSearchDemoMode : healthcareSearch
        runner({ payload, applicationId: applicantId })()
            .then(res => {
                setResponse(res)
                setHistoryTick(k => k + 1)
            })
            .catch(() => Toast({ type: 'error', message: 'Healthcare search failed' }))
            .finally(() => setLoading(false))
    }

    const openPastSearch = (entry) => {
        if (loading) return
        setLoading(true)
        setResponse(null)
        setHasSearched(true)
        setActiveSearchId(entry.searchId)
        const req = entry.request || {}
        setSearched({ name: readableName(req.name || req.npi), npi: req.npi || '', at: entry.searchedAt })
        const dob = req.dob && /^\d{8}$/.test(req.dob)
            ? `${req.dob.slice(0, 4)}-${req.dob.slice(4, 6)}-${req.dob.slice(6, 8)}`
            : ''
        const mode = (req.name || '').includes(',') ? 'individual' : (req.name ? 'business' : searchMode)
        setSearchMode(mode)
        setForm(f => ({
            ...f,
            ...nameFields(req.name, mode),
            npi: req.npi || '',
            state: req.state || '',
            dob,
            includeInactive: !!req.includeInactive,
        }))
        getHealthcareSearchResult({ applicationId: applicantId, searchId: entry.searchId, isDemoMode })()
            .then(res => setResponse(res))
            .catch(() => Toast({ type: 'error', message: "Couldn't open that search" }))
            .finally(() => setLoading(false))
    }

    const activeEntry = history.find(h => h.searchId === activeSearchId) || null
    const searchButton = (label) => (
        <Button
            onClick={onSubmit}
            loading={loading}
            disabled={!canSearch()}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            endIcon={creditUsageEnabled && healthcareSearchCost != null
                ? <CreditCost cost={healthcareSearchCost} />
                : undefined}>
            {label}
        </Button>
    )

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.title}>Healthcare Search</div>
                <div className={classes.subtitle}>
                    Screen a person or business against NPPES, HHS OIG LEIE, SAM.gov, OFAC SDN and state Medicaid
                    lists.{!applicantId && ' Quick Search results are not tied to an application.'}
                </div>
            </div>

            {/* Form and results on the left, previous searches in a rail on the right, so results
                sit right under the form without scrolling past the history. */}
            <div className={classes.layout}>
                <div style={{ minWidth: 0 }}>
                    {/* No submit button in the form (the Search button is shared with Re-run), so Enter in a
                        text box searches here. */}
                    <form onSubmit={onSubmit} className={classes.formCard}
                          onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.target.tagName === 'INPUT' && !loading) onSubmit(e)
                          }}>
                        {prefillOptions.length > 0 && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                                padding: '8px 12px', background: 'var(--bg-canvas)', borderRadius: 4,
                                fontSize: 13, flexWrap: 'wrap',
                            }}>
                                <span style={{ color: 'var(--fg-secondary)', fontWeight: 500 }}>Prefill from application:</span>
                                {prefillOptions.map((p, i) => (
                                    <button key={i} type='button' onClick={() => applyPrefill(p)}
                                            title={`Load "${p.name}" into the name field`}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                                                background: '#fff', color: 'var(--neutral-900)', border: '1px solid #0f2748',
                                                borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                            }}>
                                        {p.kind === 'business' ? <BusinessIcon style={{ fontSize: 14 }} /> : <PersonIcon style={{ fontSize: 14 }} />}
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div role='radiogroup' style={{ display: 'inline-flex', border: '1px solid #d6dde5', borderRadius: 6, overflow: 'hidden' }}>
                                    {[['individual', 'Person', PersonIcon], ['business', 'Business', BusinessIcon]].map(([mode, label, ModeIcon]) => (
                                        <button key={mode} type='button' role='radio' aria-checked={searchMode === mode}
                                                onClick={() => setSearchMode(mode)}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '6px 14px', border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                                    background: searchMode === mode ? 'var(--neutral-900)' : '#fff',
                                                    color: searchMode === mode ? '#fff' : 'var(--neutral-900)',
                                                }}>
                                            <ModeIcon style={{ fontSize: 16 }} />{label}
                                        </button>
                                    ))}
                                </div>
                                <span style={{ fontSize: 12, color: 'var(--fg-secondary)' }}>
                                    {searchMode === 'business'
                                        ? 'Add NPI or state to narrow.'
                                        : 'First or last name alone works. Add NPI or DOB to narrow.'}
                                </span>
                            </div>
                            <FormControlLabel
                                style={{ margin: 0 }}
                                control={<MuiSwitch checked={form.includeInactive}
                                                    onChange={setField('includeInactive')} size='small' />}
                                label={<span style={{ fontSize: 13 }}>Include reinstated / prior sanctions</span>}
                            />
                        </div>
                        {/* Fields wrap onto a second line when the column is narrow instead of squeezing. */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
                            {searchMode === 'business' ? (
                                <TextField label='Business name' variant='outlined' size='small' fullWidth
                                           style={{ flex: '2 1 220px' }}
                                           value={form.name} onChange={setField('name')} />
                            ) : (
                                <>
                                    <TextField label='First name' variant='outlined' size='small' fullWidth
                                               style={FIELD}
                                               value={form.firstName} onChange={setField('firstName')} />
                                    <TextField label='Last name' variant='outlined' size='small' fullWidth
                                               style={FIELD}
                                               value={form.lastName} onChange={setField('lastName')} />
                                </>
                            )}
                            <TextField label='NPI' placeholder='10 digits' variant='outlined' size='small' fullWidth
                                       style={FIELD}
                                       inputProps={{ maxLength: 10 }}
                                       value={form.npi} onChange={setField('npi')} />
                            <TextField label='State' select variant='outlined' size='small' fullWidth
                                       style={{ flex: '1 1 90px' }}
                                       value={form.state} onChange={setField('state')}>
                                <MenuItem value=''>Any</MenuItem>
                                {US_STATES.map(st => <MenuItem key={st} value={st}>{st}</MenuItem>)}
                            </TextField>
                            {searchMode !== 'business' && (
                                <TextField label='Date of birth' type='date' InputLabelProps={{ shrink: true }}
                                           variant='outlined' size='small' fullWidth style={FIELD}
                                           value={form.dob} onChange={setField('dob')} />
                            )}
                            {searchButton('Search')}
                        </div>
                    </form>

                    {activeEntry && !loading && (
                        <div className={classes.storedBanner}>
                            <span>
                                Stored result from {fmtShortDateTime(activeEntry.searchedAt)}
                                {activeEntry.searchedBy ? ` by ${activeEntry.searchedBy}` : ''}
                            </span>
                            {searchButton('Re-run')}
                        </div>
                    )}

                    {loading && <div className={classes.empty}>Searching all 5 sources…</div>}
                    {!loading && !response && (
                        <div className={classes.empty}>
                            <strong style={{ display: 'block', color: 'var(--neutral-900)', marginBottom: 4 }}>
                                Results appear here, under the search
                            </strong>
                            Each search checks all five sources and uses 1 credit. To see a past result without a
                            credit, open it from Previous searches.
                        </div>
                    )}
                    {hasSearched && !loading && response && (
                        <Results response={response} classes={classes} searched={searched} />
                    )}

                    {applicantId && savedRecords.length > 0 && (
                        <SavedRecordsPanel records={savedRecords} onDelete={handleDeleteSaved} />
                    )}
                </div>

                <SearchRail searches={history} activeSearchId={activeSearchId} onOpen={openPastSearch} />
            </div>
        </div>
    )
}

// Stored and sent as "LAST, FIRST"; shown the way it reads.
const readableName = (name) => {
    if (!name || !name.includes(',')) return name || ''
    const [last, first] = name.split(',').map(x => x.trim())
    return [first, last].filter(Boolean).join(' ')
}

// One tab per source, OFAC first. Quick Search is a lookup, so no decisions here.
const Results = ({ response, classes, searched }) => (
    <>
        <div className={classes.resultsHeader}>
            <div className={classes.resultsCount}>Results for {searched?.name || 'this search'}</div>
            <div className={classes.resultsBreakdown}>
                {searched?.at ? `Searched ${fmtShortDateTime(searched.at)} · ` : ''}5 sources
            </div>
        </div>
        <SourceTabs
            key={searched?.at}
            response={response}
            searchedName={searched?.name}
            searchedNpi={searched?.npi}
            searchedAt={searched?.at}
            readOnly
        />
    </>
)

const cardBoxStyle = (status) => ({
    background: '#fff',
    border: `1px solid ${
        status === 'EXCLUDED' ? '#e6b8b8'
      : status === 'PRIOR_SANCTIONS' ? '#e8d7a1'
      : '#d6dde5'}`,
    borderLeft: `4px solid ${
        status === 'EXCLUDED' ? 'var(--status-declined)'
      : status === 'PRIOR_SANCTIONS' ? 'var(--badge-watch-ink)'
      : 'var(--status-approved)'}`,
    borderRadius: 6,
    padding: 16,
    marginBottom: 12,
})

// `actions` replaces the Save button (the application tab passes its own decision buttons);
// `detail` replaces the plain hit list when the card is expanded.
const IdentityCard = ({ card, embeddedInApplication, saved, onSave, actions, detail, defaultExpanded = false }) => {
    const [expanded, setExpanded] = React.useState(defaultExpanded)
    const label = card.status === 'EXCLUDED'         ? 'EXCLUDED'
                : card.status === 'PRIOR_SANCTIONS'  ? 'PRIOR SANCTIONS'
                : 'IDENTITY ONLY'
    const labelBg = card.status === 'EXCLUDED'         ? { bg: 'var(--status-declined)', fg: '#fff' }
                  : card.status === 'PRIOR_SANCTIONS'  ? { bg: 'var(--badge-watch-ink)', fg: '#fff' }
                  : { bg: '#e6f4ea', fg: '#1e6132' }

    return (
        <div style={cardBoxStyle(card.status)}>
            {/* The whole header expands the card; Save and the toggle stop the click themselves. */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}
                 onClick={() => setExpanded(x => !x)}>
                {card.entity
                    ? <BusinessIcon style={{ color: 'var(--fg-secondary)', marginTop: 2 }} />
                    : <PersonIcon style={{ color: 'var(--fg-secondary)', marginTop: 2 }} />}
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--neutral-900)' }}>
                            {card.displayName || '(unknown)'}
                        </div>
                        <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 3,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '.03em',
                            background: labelBg.bg,
                            color: labelBg.fg,
                        }}>{label}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--fg-secondary)', marginTop: 4, flexWrap: 'wrap' }}>
                        {card.npi && <span>NPI: <strong>{card.npi}</strong></span>}
                        {card.providerType && <span>{card.providerType}</span>}
                        {card.credential && <span>{card.credential}</span>}
                        {card.state && <span>State: {card.state}</span>}
                        {card.dob && <span>DOB: {formatDob(card.dob)}</span>}
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                    {actions && <div onClick={(e) => e.stopPropagation()}>{actions}</div>}
                    {!actions && embeddedInApplication && (
                        <div
                            role='button'
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); if (!saved && onSave) onSave(card) }}
                            onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && !saved && onSave) {
                                    e.preventDefault(); onSave(card)
                                }
                            }}
                            title={saved
                                ? 'Already saved to this application'
                                : 'Save this identity to the application record'}
                            style={{
                                padding: '4px 12px',
                                borderRadius: 3,
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: '.03em',
                                cursor: saved ? 'default' : 'pointer',
                                userSelect: 'none',
                                background: saved ? '#e6f4ea' : 'var(--neutral-900)',
                                color: saved ? '#1e6132' : '#fff',
                                border: saved ? '1px solid #b7ddc1' : 'none',
                            }}>
                            {saved ? '✓ SAVED' : '+ SAVE'}
                        </div>
                    )}
                    <div
                        role='button'
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setExpanded(x => !x) }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(x => !x) }
                        }}
                        style={{
                            padding: '4px 10px', borderRadius: 3, cursor: 'pointer',
                            fontSize: 12, color: 'var(--neutral-900)', border: '1px solid #d6dde5',
                            userSelect: 'none',
                        }}>
                        {expanded ? 'Hide sources' : `${card.hits?.length || 0} ${card.hits?.length === 1 ? 'source' : 'sources'}`}
                    </div>
                </div>
            </div>

            {expanded && (
                <div style={{ marginTop: 12, borderTop: '1px solid #eef1f5', paddingTop: 12 }}>
                    {detail || (card.hits || []).map((h, i) => <HitRow key={i} hit={h} />)}
                </div>
            )}
        </div>
    )
}

const sourceColor = {
    NPPES:  'var(--rox-lilac-ink)',
    LEIE:   'var(--status-declined)',
    SAMGOV: '#6a1b9a',
    OFAC:   '#00695c',
    STATE_MEDICAID: '#8a4b08',
}

const SOURCE_LABELS = { STATE_MEDICAID: 'State Medicaid' }
const sourceLabel = (source) => SOURCE_LABELS[source] || source
// A State Medicaid hit names its listing state ("TX Medicaid"), which is what matters on a card.
const hitLabel = (hit) => (hit.source === 'STATE_MEDICAID' && hit.excludingAuthority) || sourceLabel(hit.source)

const Row = ({ label, value }) => value == null || value === '' ? null : (
    <div><strong>{label}:</strong> {value}</div>
)

// Plain-English expansion of the LEIE 1128-series statute codes. LEIE has no free-text
// narrative field; the exclusion type IS the reason, in encoded form. Same for SAM.gov's
// most-common HHS-referred codes.
const EXCLUSION_TYPE_DESCRIPTIONS = {
    '1128a1': 'Mandatory exclusion — Conviction of program-related crime (Medicare/Medicaid).',
    '1128a2': 'Mandatory exclusion — Conviction related to patient abuse or neglect.',
    '1128a3': 'Mandatory exclusion — Felony conviction for health care fraud.',
    '1128a4': 'Mandatory exclusion — Felony conviction relating to controlled substances.',
    '1128b1': 'Permissive exclusion — Misdemeanor conviction for health care fraud.',
    '1128b2': 'Permissive exclusion — Conviction relating to obstruction of an investigation.',
    '1128b3': 'Permissive exclusion — Misdemeanor conviction relating to controlled substances.',
    '1128b4': 'Permissive exclusion — License revocation, suspension, or surrender for professional conduct.',
    '1128b5': 'Permissive exclusion — Exclusion or suspension under a federal or state health care program.',
    '1128b6': 'Permissive exclusion — Claims for excessive charges or unnecessary services.',
    '1128b7': 'Permissive exclusion — Fraud, kickbacks, and other prohibited activities.',
    '1128b8': 'Permissive exclusion — Entity controlled by a sanctioned individual.',
    '1128b14': 'Permissive exclusion — Default on health education loan/scholarship obligations.',
    '1128b15': 'Permissive exclusion — Individuals controlling a sanctioned entity.',
    '1128b16': 'Permissive exclusion — Making false statements or misrepresentations.',
    // SAM.gov surfaces HHS-driven exclusions under a narrative label rather than a code;
    // fall through when the type doesn't match, and the raw type stays visible.
    'Prohibition/Restriction': 'Barred from receiving federal contracts, grants, or benefits — reciprocal effect across agencies.',
    'Ineligible (Proceedings Pending)': 'Excluded while enforcement proceedings are pending.',
}

const describeExclusionType = (t) => t ? EXCLUSION_TYPE_DESCRIPTIONS[t] : null

const HitRow = ({ hit }) => (
    <div style={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #f2f4f7',
        fontSize: 13,
    }}>
        <div>
            <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 3,
                background: sourceColor[hit.source] || 'var(--fg-secondary)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.03em',
            }}>{hitLabel(hit)}</span>
            {!hit.active && (
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--badge-watch-ink)', fontWeight: 600 }}>
                    Inactive
                </div>
            )}
        </div>
        <div style={{ color: '#37414d', lineHeight: 1.55 }}>
            {/* Exclusion metadata (LEIE + SAMGOV + OFAC). OFAC's type is the lowercase SDN_Type
                ("entity"/"individual") — capitalize it for display. */}
            <Row label='Type' value={hit.source === 'OFAC' && hit.exclusionType
                ? hit.exclusionType.charAt(0).toUpperCase() + hit.exclusionType.slice(1)
                : hit.exclusionType} />
            {describeExclusionType(hit.exclusionType) && (
                <div style={{
                    margin: '2px 0 6px',
                    padding: '6px 8px',
                    background: '#eef4fb',
                    borderLeft: '3px solid #1976d2',
                    fontSize: 12,
                    color: '#0d3c61',
                }}>
                    {describeExclusionType(hit.exclusionType)}
                </div>
            )}
            <Row label='Program' value={hit.exclusionProgram} />
            <Row label='Classification' value={hit.classificationType} />
            <Row label='Authority' value={hit.excludingAuthority} />
            <Row label='Agency code' value={hit.excludingAgencyCode} />
            <Row label='Effective' value={fmtDate(hit.effectiveDate)} />
            {hit.reinstatementDate && (
                <Row label={hit.source === 'SAMGOV' ? 'Termination' : 'Reinstatement'}
                     value={fmtDate(hit.reinstatementDate)} />
            )}
            <Row label='Termination type' value={hit.terminationType} />
            <Row label='Record status' value={hit.recordStatus} />

            {/* LEIE-specific */}
            <Row label='Specialty' value={hit.specialty} />
            <Row label='UPIN' value={hit.upin} />
            {hit.waiverDate && (
                <Row label='Waiver'
                     value={`${fmtDate(hit.waiverDate)}${hit.waiverState ? ' (' + hit.waiverState + ')' : ''}`} />
            )}

            {/* SAM.gov-specific */}
            <Row label='UEI (SAM)' value={hit.ueiSam} />
            <Row label='CAGE' value={hit.cageCode} />

            {/* NPPES identity */}
            <Row label='Entity type' value={hit.entityType === '1' ? 'Individual' : hit.entityType === '2' ? 'Organization' : hit.entityType} />
            <Row label='Sole proprietor' value={hit.soleProprietor} />
            <Row label='Provider type' value={hit.providerType} />
            <Row label='Taxonomy code' value={hit.taxonomyCode} />
            <Row label='License' value={hit.licenseNumber} />
            <Row label='Phone' value={hit.phone} />
            <Row label='Last updated' value={fmtDate(hit.lastUpdatedDate)} />

            {/* Free-text narrative + address always last */}
            {hit.reason && (
                <div style={{ marginTop: 6, padding: '6px 8px', background: 'var(--bg-canvas)', borderRadius: 3 }}>
                    {hit.reason}
                </div>
            )}
            {hit.address && (
                <div style={{ marginTop: 6, color: 'var(--fg-secondary)', fontSize: 12 }}>{hit.address}</div>
            )}
        </div>
    </div>
)

const fmtDate = (d) => {
    // Normalize the mixed date shapes each source hands back:
    // LEIE uses YYYYMMDD, NPPES uses YYYY-MM-DD, SAM.gov gives ISO — display as YYYY-MM-DD.
    if (!d) return d
    if (/^\d{8}$/.test(d)) return `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}`
    return d
}

const describeOutcome = (entry) => {
    if (!entry.totalCards) return 'No matches'
    const parts = [`${entry.totalCards} ${entry.totalCards === 1 ? 'match' : 'matches'}`]
    if (entry.excludedCount) parts.push(`${entry.excludedCount} excluded`)
    if (entry.priorSanctionsCount) parts.push(`${entry.priorSanctionsCount} prior sanctions`)
    return parts.join(' · ')
}

const fmtShortDateTime = (iso) => (iso
    ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '')

// How a past search reads in the rail: "First Last" for a person, the business name, or the NPI.
const railTitle = (req) => {
    if (!req) return 'Search'
    if (req.name) {
        if (!req.name.includes(',')) return req.name
        const [last, first] = req.name.split(',').map(x => x.trim())
        return [first, last].filter(Boolean).join(' ') || req.name
    }
    return req.npi ? `NPI ${req.npi}` : 'Search'
}

// Previous searches, always visible beside the results (newest first). Opening one replays its
// stored result: no re-run, no credit.
const SearchRail = ({ searches, activeSearchId, onOpen }) => {
    const [filter, setFilter] = React.useState('')
    const q = filter.trim().toLowerCase()
    const visible = q
        ? searches.filter(e => `${railTitle(e.request)} ${e.request?.npi || ''}`.toLowerCase().includes(q))
        : searches
    return (
        <aside style={{
            position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 8,
            maxHeight: 'calc(100vh - 140px)', padding: 16, background: '#fff', borderRadius: 6,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)', boxSizing: 'border-box',
        }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 14, color: 'var(--neutral-900)' }}>Previous searches</strong>
                <span style={{ fontSize: 12, color: 'var(--fg-secondary)' }}>{searches.length}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-secondary)' }}>Newest first</div>
            <TextField placeholder='Filter by name or NPI' variant='outlined' size='small' fullWidth
                       value={filter} onChange={(e) => setFilter(e.target.value)} />
            <div style={{ overflowY: 'auto', margin: '0 -8px' }}>
                {visible.length === 0 && (
                    <div style={{ padding: '12px 8px', fontSize: 12, color: 'var(--fg-secondary)' }}>
                        {searches.length ? 'No searches match this filter.' : 'No searches yet.'}
                    </div>
                )}
                {visible.map(entry => {
                    const active = entry.searchId === activeSearchId
                    const business = entry.request?.name && !entry.request.name.includes(',')
                    const RowIcon = business ? BusinessIcon : PersonIcon
                    return (
                        <button key={entry.searchId} type='button' onClick={() => onOpen(entry)}
                                title='Show the stored result (no credit)'
                                style={{
                                    display: 'flex', gap: 8, width: '100%', padding: '8px', textAlign: 'left',
                                    background: active ? 'var(--bg-canvas)' : 'transparent',
                                    border: 0, borderLeft: `3px solid ${active ? 'var(--neutral-900)' : 'transparent'}`,
                                    borderRadius: 4, cursor: 'pointer', fontSize: 13, color: 'var(--neutral-900)',
                                }}>
                            <RowIcon style={{ fontSize: 16, marginTop: 2, color: 'var(--fg-secondary)' }} />
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {railTitle(entry.request)}
                                </span>
                                <span style={{ fontSize: 12, color: entry.excludedCount ? 'var(--status-declined)' : 'var(--fg-secondary)' }}>
                                    {describeOutcome(entry)}
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--fg-secondary)' }}>
                                    {fmtShortDateTime(entry.searchedAt)}{entry.searchedBy ? ` · ${entry.searchedBy}` : ''}
                                </span>
                            </span>
                        </button>
                    )
                })}
            </div>
        </aside>
    )
}

// Identities saved to an application, each with its status at save time and a Remove action.
const SavedRecordsPanel = ({ records, onDelete }) => (
    <div style={{
        marginBottom: 24,
        padding: 16,
        background: 'var(--bg-canvas)',
        borderRadius: 6,
        border: '1px solid #e2e6ec',
    }}>
        <div style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--neutral-900)',
            letterSpacing: '.04em',
            marginBottom: 12,
            textTransform: 'uppercase',
        }}>
            Saved to this application ({records.length})
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {records.map(r => <SavedRecordRow key={r.recordId} record={r} onDelete={onDelete} />)}
        </div>
    </div>
)

/**
 * One saved-record row — collapsed by default (status pill + summary + Remove), expandable to
 * re-hydrate the full card snapshot with every source hit and every field it carried at the
 * moment of save. The snapshot is authoritative for that save — if a source has since
 * reinstated the provider, this UI still shows what the underwriter saw when the decision was
 * made, matching the audit-trail intent of persisting card_snapshot_json server-side.
 */
const SavedRecordRow = ({ record, onDelete }) => {
    const [expanded, setExpanded] = React.useState(false)
    const snapshot = React.useMemo(() => {
        if (!record.cardSnapshotJson) return null
        try { return JSON.parse(record.cardSnapshotJson) }
        catch (_) { return null }
    }, [record.cardSnapshotJson])
    const hits = snapshot?.hits || []

    const statusColors = record.status === 'EXCLUDED'         ? { bg: 'var(--status-declined)', fg: '#fff' }
                       : record.status === 'PRIOR_SANCTIONS'  ? { bg: 'var(--badge-watch-ink)', fg: '#fff' }
                       : { bg: '#e6f4ea', fg: '#1e6132' }
    const statusLabel = record.status === 'EXCLUDED'         ? 'EXCLUDED'
                      : record.status === 'PRIOR_SANCTIONS'  ? 'PRIOR SANCTIONS'
                      : 'IDENTITY ONLY'

    return (
        <div style={{
            background: '#fff',
            padding: '10px 12px',
            borderRadius: 4,
            border: '1px solid #e2e6ec',
        }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr auto auto',
                gap: 12,
                alignItems: 'center',
                fontSize: 13,
            }}>
                <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 3,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '.03em',
                    background: statusColors.bg,
                    color: statusColors.fg,
                    textAlign: 'center',
                }}>{statusLabel}</span>
                <div>
                    <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>{record.matchedName || '(unknown)'}</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-secondary)', marginTop: 2 }}>
                        {record.npi && <>NPI {record.npi} · </>}
                        {record.providerType && <>{record.providerType} · </>}
                        {record.state && <>State {record.state} · </>}
                        {record.credential && <>{record.credential} · </>}
                        Saved {record.createdAt ? new Date(record.createdAt).toLocaleString() : 'recently'}
                    </div>
                    {record.notes && (
                        <div style={{ marginTop: 4, fontSize: 12, color: '#37414d', fontStyle: 'italic' }}>
                            "{record.notes}"
                        </div>
                    )}
                </div>
                <div
                    role='button'
                    tabIndex={0}
                    onClick={() => setExpanded(x => !x)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(x => !x) }
                    }}
                    style={{
                        padding: '4px 10px', borderRadius: 3, cursor: 'pointer',
                        fontSize: 12, color: 'var(--neutral-900)', border: '1px solid #d6dde5',
                        userSelect: 'none', background: '#fff',
                    }}>
                    {expanded ? 'Hide sources' : `${hits.length} ${hits.length === 1 ? 'source' : 'sources'}`}
                </div>
                <div
                    role='button'
                    tabIndex={0}
                    onClick={() => onDelete && onDelete(record.recordId, record.matchedName)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); onDelete && onDelete(record.recordId, record.matchedName)
                        }
                    }}
                    title='Remove from saved records'
                    style={{
                        padding: '4px 10px',
                        borderRadius: 3,
                        fontSize: 12,
                        color: 'var(--status-declined)',
                        border: '1px solid #e6b8b8',
                        cursor: 'pointer',
                        userSelect: 'none',
                        background: '#fff',
                    }}>
                    Remove
                </div>
            </div>

            {expanded && (
                <div style={{ marginTop: 14, borderTop: '1px solid #eef1f5', paddingTop: 14 }}>
                    {snapshot
                        ? <HealthcareCheckDetail card={snapshot} />
                        : <div style={{ fontSize: 12, color: 'var(--status-declined)' }}>
                            Snapshot data could not be parsed — the underlying JSON may be malformed.
                          </div>}
                </div>
            )}
        </div>
    )
}

const formatDob = (dob) => {
    // LEIE stores DOB as YYYYMMDD; display as YYYY-MM-DD.
    if (!dob) return dob
    if (/^\d{8}$/.test(dob)) return `${dob.slice(0,4)}-${dob.slice(4,6)}-${dob.slice(6,8)}`
    return dob
}

// Shared with the per-entity application tab (EntityBoard).
export { IdentityCard, HitRow, US_STATES }

export default HealthcareSearch
